import Modal from "Common/Components/Modal";
import { useFormik, FormikErrors } from "formik";
import * as Yup from "yup";
import React, { useState } from "react";
import { Edit, FileEdit } from "lucide-react";
import { useAppServices } from "hook/services";
import Select from "react-select";
interface TaskFormValues {
  title: string;
  description: string;
  dueDate: string;
  completed: boolean;
  assignedTo: string | null;
}

interface TaskEditModalProps {
  handleRefresh: () => void;
  users: {
    label: string;
    value: string;
  }[];
  editData: {
    _id?: string;
    title?: string;
    description?: string;
    dueDate?: string;
    completed?: boolean;
    assignedTo?: string | null;
  } | null;
}

const TaskEditModal: React.FC<TaskEditModalProps> = ({
  handleRefresh,
  editData,
  users,
}) => {
  const AppService = useAppServices();
  const [show, setShow] = useState(false);

  const toggle = () => setShow(!show);
  // ✅ Submit handler
  const handleSubmit = async (values: TaskFormValues) => {
    const payload = {
      ...values,
      _id: editData?._id,
    };

    const { response } = await AppService.task.update({
      payload,
      toaster: true,
    });

    if (response) {
      toggle();
      handleRefresh();
    }
  };

  // ✅ Formik setup with types
  const formik = useFormik<TaskFormValues>({
    enableReinitialize: true,
    initialValues: {
      title: editData?.title || "",
      description: editData?.description || "",
      dueDate: editData?.dueDate || "",
      assignedTo: editData?.assignedTo || null,
      completed: editData?.completed || false,
    },
    validationSchema: Yup.object({
      title: Yup.string()
        .trim()
        .min(2, "Task title must be at least 2 characters long")
        .max(200, "Task title must be less than 200 characters")
        .required("Please enter a task title"),

      description: Yup.string()
        .trim()
        .max(2000, "Description must be less than 2000 characters"),

      dueDate: Yup.date()
        .required("Please select a due date")
        .typeError("Invalid date format"),

      assignedTo: Yup.string().nullable(),
      completed: Yup.boolean(),
    }),
    onSubmit: handleSubmit,
  });

  return (
    <>
      <button
        onClick={toggle}
        className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
        title="Edit"
      >
        <Edit className="size-4 text-slate-500 dark:text-slate-400" />
      </button>
      <Modal
        show={show}
        onHide={toggle}
        id="taskModal"
        modal-center="true"
        className="fixed flex flex-col transition-all duration-300 ease-in-out left-1/2 z-drawer -translate-x-1/2 -translate-y-1/2"
        dialogClassName="w-screen md:w-[40rem] bg-white dark:bg-slate-800 shadow-lg rounded-md"
      >
        {/* Header */}
        <Modal.Header className="flex items-center justify-between p-4 border-b dark:border-slate-700">
          <Modal.Title className="text-lg font-semibold flex items-center gap-2 text-slate-800 dark:text-slate-200">
            <FileEdit className="w-5 h-5 text-blue-500" />
            Edit Task
          </Modal.Title>
        </Modal.Header>

        {/* Body */}
        <Modal.Body className="max-h-[calc(100vh-180px)] p-4 overflow-y-auto">
          <form onSubmit={formik.handleSubmit} className="space-y-4">
            {/* Title */}
            <div>
              <label className="block mb-1 text-sm font-medium text-slate-700 dark:text-slate-200">
                Title
              </label>
              <input
                type="text"
                name="title"
                placeholder="Enter task title"
                className="form-input w-full dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder:text-slate-400"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.title}
              />
              {formik.touched.title && formik.errors.title && (
                <p className="text-red-400 dark:text-red-300 text-sm mt-1">
                  {formik.errors.title}
                </p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block mb-1 text-sm font-medium text-slate-700 dark:text-slate-200">
                Description
              </label>
              <textarea
                name="description"
                placeholder="Enter task description (optional)"
                className="form-textarea w-full h-24 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder:text-slate-400"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.description}
              />
              {formik.touched.description && formik.errors.description && (
                <p className="text-red-400 dark:text-red-300 text-sm mt-1">
                  {formik.errors.description}
                </p>
              )}
            </div>

            {/* Due Date */}
            <div>
              <label className="block mb-1 text-sm font-medium text-slate-700 dark:text-slate-200">
                Due Date
              </label>
              <input
                type="date"
                name="dueDate"
                className="form-input w-full dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.dueDate?.split("T")[0] || ""}
              />
              {formik.touched.dueDate && formik.errors.dueDate && (
                <p className="text-red-400 dark:text-red-300 text-sm mt-1">
                  {formik.errors.dueDate}
                </p>
              )}
            </div>

            {/* Assigned To */}
            <div>
              <label className="block mb-1 text-sm font-medium text-slate-700 dark:text-slate-200">
                Assigned To (User ID)
              </label>
              <Select
                name="assignedTo"
                value={
                  users.find(
                    (option) => option.value === formik.values.assignedTo
                  ) || null
                }
                onChange={(selected) =>
                  formik.setFieldValue(
                    "assignedTo",
                    selected ? selected.value : null
                  )
                }
                options={users}
                placeholder="Assign to (optional)"
                isClearable
              />
              {formik.touched.assignedTo && formik.errors.assignedTo && (
                <p className="text-red-400 dark:text-red-300 text-sm mt-1">
                  {formik.errors.assignedTo}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="text-red-500 dark:text-red-400 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 btn hover:text-red-600 dark:hover:text-red-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                onClick={() => {
                  formik.resetForm();
                  toggle();
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="text-white btn bg-custom-500 border-custom-500 hover:bg-custom-600 dark:bg-custom-500 dark:border-custom-500 dark:hover:bg-custom-600"
              >
                Edit Task
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default TaskEditModal;
